import { useState, useCallback } from 'react';
import {
  LocationConfig,
  IntegrationConfig,
  SubmitStatus,
} from '../types/AccuLynxTypes';
import { ACCULYNX_STAGES } from '../constants/AccuLynxConstants';
import { INTEGRATIONS_API_BASE } from 'application/config/apiConfig';

export const useAccuLynxActions = (
  config: IntegrationConfig,
  setConfig: (
    config: IntegrationConfig | ((prev: IntegrationConfig) => IntegrationConfig)
  ) => void,
  paivStages: any[],
  circles: any[],
  isEditing: boolean,
  savedConfig: IntegrationConfig,
  setIsEditing: (editing: boolean) => void,
  setSavedConfig: (config: IntegrationConfig) => void,
  stageCategories: any[] = []
) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>({
    type: undefined,
    message: '',
  });

  // derived flags can be computed where needed

  // Add a new location
  const addLocation = useCallback(() => {
    console.log('Adding new location');
    console.log('Available stages:', paivStages);

    setConfig((prev) => {
      // Determine default stageCategoryID:
      // 1. Use the first existing location's stageCategoryID if available
      // 2. Otherwise, use the first category from stageCategories (usually "Contacts")
      const defaultStageCategoryID =
        prev.locations.find((loc) => loc?.stageCategoryID)?.stageCategoryID ||
        (stageCategories.length > 0 ? stageCategories[0]?.id : '');

      return {
        ...prev,
        locations: [
          ...prev.locations,
          {
            apiKey: '',
            paivStage: paivStages[0]?.title || '',
            paivStageId: paivStages[0]?.id || '',
            accessToken: '',
            refreshToken: '',
            tokenExpiration: '',
            connectionStatus: 'untested',
            stageCategoryID: defaultStageCategoryID,
            stageMappings: ACCULYNX_STAGES.map((stage) => ({
              accuLynxStage: stage,
              paivStage: '',
              paivStageId: '',
            })),
          },
        ],
      };
    });
  }, [paivStages, setConfig, stageCategories]);

  // Remove a location from local config
  const removeLocation = useCallback(
    (index: number) => {
      setConfig((prev) => ({
        ...prev,
        locations: prev.locations.filter((_, i) => i !== index),
      }));
    },
    [setConfig]
  );

  // Remove an individual integration from database
  const removeIntegration = useCallback(
    async (locationIndex: number, onConfirm: () => void): Promise<void> => {
      const location = config.locations[locationIndex];
      if (!location?.circleId) return;

      // Call the confirmation callback - the parent component will show the modal
      onConfirm();
    },
    [config.locations]
  );

  // Actually perform the integration removal after confirmation
  const performIntegrationRemoval = useCallback(
    async (locationIndex: number): Promise<void> => {
      try {
        const orgID = (window as any).me.orgID;
        const userId = (window as any).me.userID;

        // Create a new locations array without the removed location
        const updatedLocations = config.locations.filter(
          (_, i) => i !== locationIndex
        );

        // Build payload with ids so backend can update/deactivate correctly
        const payload = {
          orgID,
          locations: updatedLocations.map((loc) => ({
            id: (loc as any)?.id,
            apiKey: loc.apiKey,
            circleId: loc.circleId,
            circleName:
              circles.find((c: any) => c.id === loc.circleId)?.name ||
              (loc as any)?.circleName ||
              'Unknown',
            stageMappings: loc.stageMappings,
            circleLevelID: (loc as any)?.circleLevelID, // Add this
          })),
          userId,
        };

        console.log(
          '🔍 Remove Integration payload:',
          JSON.stringify(payload, null, 2)
        );

        const apiUrl = `${INTEGRATIONS_API_BASE}/setup-acculynx-integration`;
        console.log('🔍 API URL:', apiUrl);
        console.log(
          '🔍 Environment variable:',
          process.env.REACT_APP_ACCULYNX_API_ENDPOINT
        );

        const res = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${(window as any).me.accessToken}`,
          },
          body: JSON.stringify(payload),
        });

        console.log('🔍 Response status:', res.status);
        console.log(
          '🔍 Response headers:',
          Object.fromEntries(res.headers.entries())
        );

        const responseText = await res.text();
        console.log('🔍 Raw response:', responseText);

        let result;
        try {
          result = JSON.parse(responseText);
        } catch (parseError) {
          console.error(
            '❌ Failed to parse response as JSON:',
            responseText.substring(0, 200)
          );
          throw new Error(
            `API returned non-JSON response: ${responseText.substring(0, 100)}`
          );
        }
        if (!res.ok || !result?.success) {
          throw new Error(result?.error || 'Failed to remove integration');
        }

        // Update local state
        setConfig((prev) => ({
          ...prev,
          locations: updatedLocations,
        }));

        // If this was the last location, clear everything and show fresh start
        if (updatedLocations.length === 0) {
          setConfig({ locations: [], stageMappings: [] });
          setSavedConfig({ locations: [], stageMappings: [] });
          setIsEditing(false);
        } else {
          // Persist saved config as well
          setSavedConfig({ ...savedConfig, locations: updatedLocations });
        }

        setSubmitStatus({
          type: 'success',
          message: 'Integration removed successfully',
        });
      } catch (error: any) {
        console.error('Error removing integration:', error);
        setSubmitStatus({
          type: 'error',
          message: error?.message || 'Failed to remove integration',
        });
      }
    },
    [
      config.locations,
      setConfig,
      setIsEditing,
      setSubmitStatus,
      setSavedConfig,
      savedConfig,
      circles,
    ]
  );

  // Update location fields
  const updateLocation = useCallback(
    (index: number, field: keyof LocationConfig | PropertyKey, value: any) => {
      setConfig((prev) => {
        if (field === 'paivStage' && index === 0) {
          const selectedStage = paivStages.find(
            (stage) => stage.title === value
          );
          return {
            ...prev,
            locations: prev.locations.map((loc) => ({
              ...loc,
              paivStage: value,
              paivStageId: selectedStage?.id || '',
            })),
          };
        }

        // When selecting a team, update circleId only (preserve existing id)
        if (field === 'circleId') {
          return {
            ...prev,
            locations: prev.locations.map((loc, i) =>
              i === index
                ? {
                    ...loc,
                    circleId: value as string,
                  }
                : loc
            ),
          };
        }

        return {
          ...prev,
          locations: prev.locations.map((loc, i) =>
            i === index
              ? { ...loc, [field as keyof LocationConfig]: value }
              : loc
          ),
        };
      });
    },
    [paivStages, setConfig, circles]
  );

  // Handle API key changes
  const handleApiKeyChange = useCallback(
    (index: number, value: string) => {
      updateLocation(index, 'apiKey', value);
      // When user edits the key, mark as untested until save validates
      updateLocation(
        index,
        'connectionStatus',
        value.trim() ? 'untested' : 'untested'
      );
    },
    [updateLocation]
  );

  // Update stage mappings
  const updateStageMapping = useCallback(
    (
      locationIndex: number,
      accuLynxStage: string,
      paivStageTitle: string
    ): void => {
      console.log('Updating stage mapping:', {
        locationIndex,
        accuLynxStage,
        paivStageTitle,
        paivStagesCount: paivStages.length,
        paivStagesTitles: paivStages.map((s) => s.title),
      });

      const selectedStage = paivStages.find(
        (stage) => stage.title === paivStageTitle
      );
      console.log('Selected stage:', {
        selectedStage,
        foundById: selectedStage?.id,
        foundByTitle: selectedStage?.title,
        exactMatch: paivStages.find((s) => s.title === paivStageTitle),
        trimmedMatch: paivStages.find(
          (s) => s.title.trim() === paivStageTitle.trim()
        ),
      });

      setConfig((prev) => ({
        ...prev,
        locations: prev.locations.map((loc, idx) => {
          if (idx !== locationIndex) return loc;

          const existing = loc.stageMappings || [];
          const hasEntry = existing.some(
            (m) => m?.accuLynxStage === accuLynxStage
          );

          const nextStageMappings = hasEntry
            ? existing.map((m) =>
                m.accuLynxStage === accuLynxStage
                  ? {
                      ...m,
                      paivStage: paivStageTitle,
                      paivStageId: String(selectedStage?.id ?? ''),
                    }
                  : m
              )
            : [
                ...existing,
                {
                  accuLynxStage,
                  paivStage: paivStageTitle,
                  paivStageId: String(selectedStage?.id ?? ''),
                },
              ];

          console.log('Updated stage mappings:', {
            locationIndex: idx,
            accuLynxStage,
            newMapping: nextStageMappings.find(
              (m) => m.accuLynxStage === accuLynxStage
            ),
          });

          return {
            ...loc,
            stageMappings: nextStageMappings,
          };
        }),
      }));
    },
    [paivStages, setConfig]
  );

  // Validate configuration
  const validateConfig = useCallback((): boolean => {
    if (!config.locations || config.locations.length === 0) {
      return false;
    }

    // All required fields
    const fieldsOk = config.locations.every((loc) => {
      if (!loc) return false;
      return Boolean(loc.apiKey?.trim() && loc.circleId?.trim());
    });
    if (!fieldsOk) return false;

    // Every AccuLynx stage must be mapped with a valid paivStage and paivStageId
    const allStagesMapped = config.locations.every((loc) => {
      const byStage = new Map(
        (loc.stageMappings || []).map((m) => [m.accuLynxStage, m])
      );
      return ACCULYNX_STAGES.every((s) => {
        const m = byStage.get(s);
        const hasTitle =
          typeof m?.paivStage === 'string' && m.paivStage.trim().length > 0;
        const idVal: any = m?.paivStageId;
        const hasId =
          typeof idVal === 'string'
            ? idVal.trim().length > 0
            : idVal !== undefined && idVal !== null;
        return Boolean(hasTitle && hasId);
      });
    });
    if (!allStagesMapped) return false;

    // Unique teams
    const circleIds = config.locations
      .map((l) => l?.circleId)
      .filter((id): id is string => Boolean(id && id.trim()));
    if (new Set(circleIds).size !== circleIds.length) return false;

    return true;
  }, [config]);

  // Handle form submission
  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      if (e) e.preventDefault();

      setIsSubmitting(true);
      setSubmitStatus({ type: undefined, message: '' });

      try {
        const orgID = (window as any).me.orgID;
        console.log('Current orgID:', orgID);

        // Prepare data for backend API
        const integrationData = {
          orgID,
          locations: config.locations.map((location) => {
            const selectedCircle = circles.find(
              (circle) => circle.id === location.circleId
            );
            return {
              id: (location as any)?.id,
              apiKey: location.apiKey,
              circleId: location.circleId,
              circleName:
                selectedCircle?.name ||
                (location as any)?.circleName ||
                'Unknown',
              stageMappings: location.stageMappings,
              stageCategoryID: (location as any)?.stageCategoryID, // Add this
              circleLevelID: (location as any)?.circleLevelID, // Add this
            };
          }),
          userId: (window as any).me.userID,
        };

        console.log('Sending integration data to backend:', integrationData);

        // Call backend API - let it handle everything (database + webhooks)
        const apiUrl = `${INTEGRATIONS_API_BASE}/setup-acculynx-integration`;
        console.log('🔍 Save API URL:', apiUrl);
        console.log(
          '🔍 Auth token:',
          (window as any).me?.accessToken ? 'Present' : 'Missing'
        );
        console.log(
          '🔍 Request payload:',
          JSON.stringify(integrationData, null, 2)
        );

        let response;
        try {
          response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${(window as any).me.accessToken}`,
            },
            body: JSON.stringify(integrationData),
          });
          console.log('🔍 Fetch successful, response status:', response.status);
        } catch (fetchError: any) {
          console.error('❌ Fetch failed:', fetchError);
          console.error('❌ Fetch error type:', fetchError?.constructor?.name);
          console.error('❌ Fetch error message:', fetchError?.message);

          // Check if it's a network error
          if (
            fetchError instanceof TypeError &&
            fetchError.message === 'Failed to fetch'
          ) {
            throw new Error(
              'Network error: Unable to connect to the API. Check your internet connection and API endpoint.'
            );
          }
          throw fetchError;
        }

        console.log('🔍 Save Response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ API Error Response:', errorText.substring(0, 200));
          throw new Error(
            `HTTP error! status: ${response.status} - ${errorText.substring(
              0,
              100
            )}`
          );
        }

        const result = await response.json();
        console.log('Backend response:', result);

        if (result.success) {
          // Build nextConfig reflecting API key validation warnings inline (non-blocking)
          let nextConfig: IntegrationConfig = config;
          if (Array.isArray(result.warnings) && result.warnings.length > 0) {
            const warned = new Set(
              result.warnings.map((w: any) => w?.locationId).filter(Boolean)
            );
            nextConfig = {
              ...config,
              locations: config.locations.map((loc) =>
                warned.has(loc.circleId)
                  ? {
                      ...loc,
                      connectionStatus: 'failed',
                      connectionError: 'API key is invalid',
                    }
                  : {
                      ...loc,
                      connectionStatus: 'connected',
                      connectionError: undefined,
                    }
              ),
            };
          } else {
            nextConfig = {
              ...config,
              locations: config.locations.map((loc) => ({
                ...loc,
                connectionStatus: 'connected',
                connectionError: undefined,
              })),
            };
          }

          setConfig(nextConfig);
          setSavedConfig(nextConfig);

          setSubmitStatus({
            type: result.warnings?.length ? 'info' : 'success',
            message: result.warnings?.length
              ? `Saved. ${result.warnings.length} API key${
                  result.warnings.length > 1 ? 's' : ''
                } may be invalid.`
              : result.message || 'Integration saved successfully!',
          });

          // Update flags
          setIsEditing(false);

          // Log webhook subscription results
          if (result.webhookUrls && result.webhookUrls.length > 0) {
            console.log('Webhook URLs set up:', result.webhookUrls);
          }

          if (result.errors && result.errors.length > 0) {
            console.warn('Some operations failed:', result.errors);
          }
        } else {
          setSubmitStatus({
            type: 'error',
            message:
              result.message || result.error || 'Failed to set up integration',
          });
        }
      } catch (error) {
        console.error('Error in handleSubmit:', error);

        let errorMessage =
          'There was an error saving your integration. Please try again or contact support.';

        // Handle network/fetch errors
        if (error instanceof Error) {
          errorMessage = `Error: ${error.message}`;
        }

        setSubmitStatus({
          type: 'error',
          message: errorMessage,
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [config, circles, setSavedConfig, setIsEditing]
  );

  // Handle cancel
  const handleCancel = useCallback(() => {
    setConfig(savedConfig);
    setIsEditing(false);
    setSubmitStatus({ type: undefined, message: '' });
  }, [savedConfig, setConfig, setIsEditing]);

  // Handle get started
  const handleGetStarted = useCallback(() => {
    console.log('handleGetStarted called');
    setIsEditing(true);
  }, [setIsEditing]);

  return {
    // State
    isSubmitting,
    submitStatus,

    // Actions
    addLocation,
    removeLocation,
    removeIntegration,
    performIntegrationRemoval,
    updateLocation,
    handleApiKeyChange,
    updateStageMapping,
    validateConfig,
    handleSubmit,
    handleCancel,
    handleGetStarted,

    // Setters
    setSubmitStatus,
  };
};
